import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { BAND_ACTIONS, IEventGuest, IEventGuestStatus } from "Bands/types";
import { useClearActionMetaOnUnMount, useGuestlistNumberError } from "Bands/utils";
import { copy } from "shared/assets";
import { APIErrorMessage, Button, ButtonContainer, Loader, Modal } from "shared/components";
import { IAPIState, IStatus, ITheme } from "shared/types";
import { dataMetaSelector, editEventGuest } from "Bands/state";
import { addTemporaryToast } from "shared/components/Toast";
import { Spacer } from "shared/layout";

const editCopy = copy.guestlistBlock.guests.edit;

interface EditGuestStatusModalProps {
  blockId: string;
  guest: IEventGuest;
  newStatus: IEventGuestStatus;
  handleClose: () => void;
}

export const EditGuestStatusModal: React.FC<EditGuestStatusModalProps> = ({
  blockId,
  guest,
  newStatus,
  handleClose,
}) => {
  const dispatch = useDispatch();
  const { api: apiMeta } = useSelector(dataMetaSelector(BAND_ACTIONS.editEventGuest));

  useClearActionMetaOnUnMount(BAND_ACTIONS.editEventGuest);

  useEffect(() => {
    if (apiMeta?.status === IAPIState.succeeded) {
      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: editCopy.messages.success,
        })
      );

      handleClose();
    }
  }, [apiMeta?.status, dispatch, handleClose]);

  const handleSubmit = () => {
    dispatch(
      editEventGuest({
        id: guest.id,
        blockId,
        data: {
          status: newStatus,
        },
      })
    );
  };

  const buttonCopy = useMemo(() => {
    switch (newStatus) {
      case IEventGuestStatus.approved:
        return editCopy.buttons.approve;
      case IEventGuestStatus.pending:
        return editCopy.buttons.setPending;
      case IEventGuestStatus.denied:
        return editCopy.buttons.deny;
      default:
        return "";
    }
  }, [newStatus]);

  const buttonStatus = useMemo(() => {
    switch (newStatus) {
      case IEventGuestStatus.denied:
        return IStatus.error;
      default:
        return undefined;
    }
  }, [newStatus]);

  const { error } = useGuestlistNumberError(apiMeta?.error);

  return (
    <Modal title={editCopy.title} handleClose={handleClose} data-testid="edit_status_modal">
      <Spacer size="s">
        <p>
          {editCopy.messages.changeStatus
            .replace("%status", newStatus)
            .replace("%guest", `${guest.firstname} ${guest.lastname}`)}
        </p>
        <div>
          <ButtonContainer>
            <Button status={buttonStatus} onClick={handleSubmit} data-testid="edit_status_button">
              <Spacer spaceDirection="horizontal" size="xs">
                <span>{buttonCopy}</span>
                {apiMeta?.status === IAPIState.pending && <Loader size="s" />}
              </Spacer>
            </Button>
            <Button
              onClick={handleClose}
              theme={ITheme.secondary}
              data-testid="cancel_edit_status_button"
            >
              {copy.form.cancel}
            </Button>
          </ButtonContainer>
        </div>
        {error ||
          (apiMeta?.error && <APIErrorMessage apiErrorMessage={error} apiError={apiMeta?.error} />)}
      </Spacer>
    </Modal>
  );
};
